import React, { useEffect, useRef, useState } from "react";

import { PodcastsOutlined as PodcastsOutlinedIcon } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import {
    Button,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Autocomplete, createFilterOptions, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";

import { ButtonVariant } from "Constant/button";
import AddComparisationModal from "pages/LeadsPages/AddComparisationModal";
import { connectionSectionDeleteButtonSx } from "pages/LeadsPages/shared/connectionSectionActionStyles";
import { Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import ClipLoader from "react-spinners/ClipLoader";

import {
    getFacebookSourceIdentity,
    isFacebookCustomType,
} from "helpers/leads/facebookSourceField";
import { saveDynamicFields } from "helpers/leads/leadConnection/postConnectionField";
import {
    applyImportedMappings,
    extractImportedMappingId,
    mergeDynamicSourceOptions,
    toDynamicRowFields,
    validateImportJson,
} from "helpers/leads/stepHelpers/fieldMappingImport";
import {
    getIsGroupChangedSourceFields,
    getTemplateDetail,
    importFieldMapping,
    saveFieldMappingAsync,
    saveTempAsync,
} from "helpers/leads/stepHelpers/step2Helpers";

import StandartButton from "components/atoms/StandartButton";
import FieldOptionsModal from "components/Leads/SharedComponents/FieldOptionsModal";
import FieldOptionsSummaryPopover from "components/Leads/SharedComponents/FieldOptionsSummaryPopover";
import TestRunModal from "components/Leads/TestRunModal";
import CreateDynamicValueModal from "components/organisms/CreateDynamicValueModal";
import WizardStepLayout from "components/organisms/WizardStepLayout";
import SwalAlertComp from "components/SwalAlert";

import "../../css/leadStep2.css";
import { getLeadTemlateName } from "../../redux/slices/leadService/leadService.asyncActions";
import { templateData } from "../../redux/slices/leadsWizardSourceDestination/leadsWizardSourceDestination.slice";
import { destinationAndSourceFields } from "../../redux/slices/step1Datas/step1Datas.slice";
import * as Styled from "./LeadWizard/LeadWizard.styled";

const LeadsStep22 = (props) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [tempName, setTempName] = useState("");
    const [allFunctions, setAllFunctions] = useState([]);
    const [optionsHover, setOptionsHover] = useState(null);
    const [isGroupState, setIsGroupState] = useState(true);
    const [sourceFieldsOptions, setSourceFieldsOptions] = useState([]);
    console.log("sourceFieldsOptions", sourceFieldsOptions);
    const [isLoading, setIsLoading] = useState(false);
    const [sourceError, setSourceError] = useState(false);
    const [destinationError, setDestinationError] = useState(false);
    const [tempError, setTempError] = useState(false);

    useEffect(() => {
        if (props.isActive) {
            setSourceError(false);
            setDestinationError(false);
            setTempError(false);
        }
    }, [props.isActive]);
    const [isComparisationFieldModalOpen, setIsComparisationFieldModalOpen] =
        useState(null);
    const [comparisationFieldIndex, setComparisationFieldIndex] =
        useState(null);
    const [isLoadingGroupSimilarFields, setIsLoadingGroupSimilarFields] =
        useState(false);
    const [isDynamicValueOpen, setIsDynamicValueOpen] = useState(false);
    const [dynamicValueIndex, setDynamicValueIndex] = useState(null);
    const [isTestModalOpen, setIsTestModalOpen] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [isImportingMapping, setIsImportingMapping] = useState(false);
    const stepLayoutRef = useRef(null);
    const importFileInputRef = useRef(null);
    const skipFieldListResetRef = useRef(false);
    const importedDynamicNamesRef = useRef([]);
    const language = useSelector((state) => state.languagesState.languages);

    const scrollMappingToBottom = () => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                stepLayoutRef.current?.scrollToBottom("smooth");
            });
        });
    };

    const AllFields = useSelector(
        (state) => state.stepOneDatas.sourceDestinationFields,
    );

    // Create a stable filter function for destination fields autocomplete
    const destinationFilterOptions = createFilterOptions({
        matchFrom: "any",
        stringify: (option) => option.name,
    });

    // Memoize destination fields to prevent duplicates
    const memoizedDestinationFields = React.useMemo(() => {
        const fields = AllFields?.destination_fields || [];
        return fields;
    }, [AllFields?.destination_fields]);

    const stepOnePostDatas = useSelector((state) => state.stepOneDatas.data);
    const isFacebookSource = stepOnePostDatas?.source_type_code === "fl";

    // Facebook: identity is the Meta type token (PHONE, CREATED_UNIX_TIME, ...)
    // except for advertiser questions (type CUSTOM), which are identified by
    // their question key. Everything else: identity is the key.
    const getSourceFieldIdentity = (option) => {
        if (!option) return "";
        if (
            isFacebookSource &&
            !option.isDynamic &&
            option.type != null &&
            option.type !== ""
        ) {
            return getFacebookSourceIdentity(option);
        }
        return option.key?.toString?.() ?? "";
    };

    const resolveSourceFieldOption = (identity) => {
        if (identity == null || identity === "") return undefined;
        const asString = identity.toString();
        return sourceFieldsOptions.find(
            (field) =>
                getSourceFieldIdentity(field) === asString ||
                field.key?.toString?.() === asString,
        );
    };

    // Row identity used for UI-side bookkeeping (comparisons, badges, test
    // run). `source` is the identity; `sourceKey` is the raw key sent to the
    // API and may differ from it for Facebook groupable rows.
    const getRowIdentity = (row) => {
        if (!row) return "";
        if (typeof row.source === "string" && row.source !== "") {
            return row.source;
        }
        return row.sourceKey?.toString?.() ?? "";
    };

    const toSourceFieldIdentity = (identity) => {
        const match = resolveSourceFieldOption(identity);
        return match
            ? getSourceFieldIdentity(match)
            : (identity?.toString?.() ?? "");
    };

    const normalizeAdvancedPayload = (payload) => {
        if (!payload || !isFacebookSource) return payload || "";
        const pairs = sourceFieldsOptions
            .filter(
                (field) =>
                    !field.isDynamic &&
                    field.key != null &&
                    field.key !== "" &&
                    field.key.toString() !== getSourceFieldIdentity(field),
            )
            .map((field) => ({
                from: `{{${field.key}}}`,
                to: `{{${getSourceFieldIdentity(field)}}}`,
            }))
            .sort((a, b) => b.from.length - a.from.length);
        return pairs.reduce(
            (text, pair) => text.split(pair.from).join(pair.to),
            payload,
        );
    };

    const templatesNames = useSelector(
        (state) =>
            state.leadAllLeadServiceState.leadTemplateNameState.templateNames,
    );
    const [fieldList, setFieldList] = useState([
        {
            source: {},
            sourceType: "",
            sourceKey: "",
            options: {
                isOpen: false,
                functions: [],
                default: "",
                prefix: "",
            },
            destination: {},
        },
    ]);

    const resetOpenStates = () => {
        const updatedFieldList = fieldList.map((item) => ({
            ...item,
            options: {
                ...item.options,
                isOpen: false,
            },
        }));
        setFieldList(updatedFieldList);
    };

    useEffect(() => {
        if (
            fieldList?.length === 1 &&
            fieldList?.some(
                (item) =>
                    !item?.source ||
                    !item?.destination ||
                    !item?.sourceType ||
                    !item?.sourceKey,
            )
        ) {
            setSelectedTemplate(null);
        }
    }, [fieldList]);
    useEffect(() => {
        if (templatesNames) {
            setTempName(
                templatesNames.name ? templatesNames.name : "Template001",
            );
        }
    }, [templatesNames]);
    const toPrevious = () => {
        props.previousStep();
    };
    useEffect(() => {
        dispatch(getLeadTemlateName());
    }, [dispatch]);

    useEffect(() => {
        if (tempName === "" || !tempName) {
            setTempError(true);
        } else setTempError(false);
    }, [tempName]);

    useEffect(() => {
        const baseSourceFields = AllFields.is_group
            ? AllFields.source_fields_group
            : AllFields.source_fields;
        const keepImportedDynamicNames = skipFieldListResetRef.current;
        setSourceFieldsOptions(
            keepImportedDynamicNames
                ? mergeDynamicSourceOptions(
                      baseSourceFields,
                      importedDynamicNamesRef.current,
                  )
                : baseSourceFields,
        );
        importedDynamicNamesRef.current = [];
        let resultFunctionsChanged =
            AllFields &&
            AllFields.functions &&
            AllFields.functions.length > 0 &&
            AllFields.functions.map((item) => ({
                id: item.code,
                value: item.code,
                label: item.name,
                value_types: item.value_types,
            }));
        setAllFunctions(resultFunctionsChanged);
        if (skipFieldListResetRef.current) {
            skipFieldListResetRef.current = false;
            return;
        }
        const requiredDestinations =
            AllFields &&
            AllFields?.destination_fields?.filter((el) => el.required);
        if (
            requiredDestinations &&
            requiredDestinations?.length &&
            requiredDestinations?.length > 0
        ) {
            let initialDestinations = [];
            for (let i = 0; i < requiredDestinations?.length; i++) {
                initialDestinations.push({
                    source: {},
                    sourceType: "",
                    sourceKey: "",
                    options: {
                        isOpen: false,
                        functions: [],
                        default: "",
                        prefix: "",
                    },
                    destination: requiredDestinations[i].type,
                });
                setFieldList(initialDestinations);
            }
        } else
            setFieldList([
                {
                    source: {},
                    sourceType: "",
                    sourceKey: "",
                    options: {
                        isOpen: false,
                        functions: [],
                        default: "",
                        prefix: "",
                    },
                    destination: {},
                    sourceErrors: true,
                    destinationErrors: true,
                },
            ]);
    }, [AllFields]);

    const onChangeGroupSimilarFields = async () => {
        setIsLoadingGroupSimilarFields(true);
        setSourceFieldsOptions([]);
        let data = {
            business_id: stepOnePostDatas?.business_id ?? "",
            source_type_code: stepOnePostDatas?.source_type_code ?? "",
            is_group: !isGroupState,
            ...(stepOnePostDatas?.source_type_code === "fl" && {
                selected_pages: stepOnePostDatas?.selected_pages ?? "",
            }),
        };
        if (stepOnePostDatas?.source_type_code !== "fl") {
            data.source_id = stepOnePostDatas?.source_id ?? "";
        }
        if (stepOnePostDatas?.source_type_code === "tiktok") {
            if (props?.selectedSourceId !== undefined) {
                data.source_id = props?.selectedSourceId;
            }
        }
        let response = await getIsGroupChangedSourceFields(data);
        if (response.status) {
            setSourceFieldsOptions(response.data ?? []);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message:
                    language?.lead?.something_went_wrong ??
                    "Something went wrong!!",
                icon: "info",
            });
        }

        setIsGroupState(!isGroupState);
        setIsLoadingGroupSimilarFields(false);
    };

    /* save as template start */

    const [isSaveTemplate, setIsSaveTemplate] = useState(false);

    const checkHandlerIsSaveTemplate = () => {
        const nextValue = !isSaveTemplate;
        setIsSaveTemplate(nextValue);
        if (nextValue) {
            dispatch(getLeadTemlateName());
            scrollMappingToBottom();
        }
    };

    const [selectedTemplate, setSelectedTemplate] = useState(null);

    useEffect(() => {
        setSelectedTemplate(null);
    }, []);

    const handleSelectedTemplates = async (e) => {
        let id = e?.target?.value;
        setSelectedTemplate(id);
        let response = await getTemplateDetail(id);
        if (response.status) {
            let field_mapping = response?.data;
            let newFields = [];
            const dynamicNames = [];
            Object.keys(field_mapping).forEach((i) => {
                let item = field_mapping[i];
                let functions = [];
                const isDynamic = !item.is_static && !!item.is_dynamic;
                const dynamicName =
                    item.dynamic_field_name ||
                    item.source_field?.key?.toString();
                const useFacebookType =
                    isFacebookSource && !item.is_static && !isDynamic;
                // Facebook: template rows carry `type` (Meta enum) and `key`
                // (question key). CUSTOM rows resolve by key, others by type.
                const sourceFieldIdentity = useFacebookType
                    ? getFacebookSourceIdentity(item.source_field)
                    : item.source_field.key.toString();
                const matchedOption = sourceFieldsOptions.find(
                    (x) => getSourceFieldIdentity(x) === sourceFieldIdentity,
                );
                if (
                    matchedOption !== undefined ||
                    item.is_static ||
                    isDynamic
                ) {
                    item.fn.forEach((func) => {
                        let funcObj = allFunctions.find(
                            (x) => x.id == func?.fn_name,
                        );
                        functions.push(funcObj?.id);
                    });

                    let comparedFieldsObject = {};
                    if (Array.isArray(item.compared_mapping)) {
                        item.compared_mapping.forEach((cmpObj) => {
                            let key = Object.keys(cmpObj)[0];
                            comparedFieldsObject[key] = cmpObj[key];
                        });
                    }
                    const sourceParts = isDynamic
                        ? {
                              source: dynamicName,
                              sourceType: dynamicName,
                              sourceKey: dynamicName,
                              name: dynamicName,
                              is_dynamic: true,
                              ...toDynamicRowFields(item.dynamic_field),
                          }
                        : {
                              source: useFacebookType
                                  ? sourceFieldIdentity
                                  : item.source_field.key.toString(),
                              sourceType: useFacebookType
                                  ? (matchedOption?.type?.toString?.() ??
                                    item.source_field?.type?.toString?.() ??
                                    sourceFieldIdentity)
                                  : item.source_field.type,
                              sourceKey: useFacebookType
                                  ? (matchedOption?.key?.toString?.() ??
                                    item.source_field?.key?.toString?.() ??
                                    sourceFieldIdentity)
                                  : item.source_field.key,
                          };
                    if (isDynamic) {
                        dynamicNames.push(dynamicName);
                    }
                    newFields.push({
                        ...sourceParts,
                        custom: item?.is_static,
                        options: {
                            isOpen: false,
                            functions: functions,
                            default: item.default || "",
                            prefix: item.prefix_label || "",
                        },
                        compared_fields: comparedFieldsObject || [],
                        destination: memoizedDestinationFields?.find(
                            (destinationField) =>
                                destinationField?.type ===
                                item?.destination_field,
                        )
                            ? item?.destination_field
                            : "",
                        destinationErrors: true,
                        sourceErrors: true,
                    });
                }
            });
            setSourceFieldsOptions((prev) =>
                mergeDynamicSourceOptions(prev, dynamicNames),
            );
            setFieldList(newFields);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    /* save as template end */

    const onDynamicVariableCreate = async (newVariable) => {
        const mappingIds = Array.isArray(AllFields?.mapping_id)
            ? AllFields.mapping_id
            : AllFields?.mapping_id
              ? [AllFields.mapping_id]
              : [];

        const dynamicFieldsPayload = {
            source_destination_mappings: mappingIds,
            dynamic_fields: {
                basic_mode:
                    newVariable.mode === "basic"
                        ? [
                              {
                                  name: newVariable.name,
                                  payload: (newVariable.fields || []).map(
                                      (f) => ({
                                          key: f.value,
                                          default: f.defaultValue || null,
                                          prefix: f.prefix || null,
                                          suffix: f.suffix || null,
                                          fn: (f.fn || []).map((fnItem) =>
                                              typeof fnItem === "string"
                                                  ? {
                                                        fn_name: fnItem,
                                                        fn_values: {},
                                                    }
                                                  : fnItem,
                                          ),
                                      }),
                                  ),
                              },
                          ]
                        : [],
                advanced_mode:
                    newVariable.mode === "advanced"
                        ? [
                              {
                                  name: newVariable.name,
                                  payload: newVariable.payload,
                              },
                          ]
                        : [],
            },
        };

        await saveDynamicFields(dynamicFieldsPayload);

        if (typeof newVariable.index === "number") {
            const oldName = fieldList[newVariable.index]?.name;
            setSourceFieldsOptions((prev) =>
                prev.map((opt) =>
                    opt.key === oldName
                        ? {
                              ...opt,
                              key: newVariable.name,
                              label: newVariable.name,
                          }
                        : opt,
                ),
            );
            setFieldList((prev) => {
                const newList = [...prev];
                newList[newVariable.index] = {
                    ...newList[newVariable.index],
                    name: newVariable.name,
                    source: newVariable.name,
                    sourceType: newVariable.name,
                    sourceKey: newVariable.name,
                    dynamicMode: newVariable.mode,
                    ...(newVariable.mode === "advanced"
                        ? {
                              advancedPayload: newVariable.payload,
                              fields: [],
                          }
                        : {
                              fields: newVariable.fields,
                          }),
                };
                return newList;
            });
            setIsDynamicValueOpen(false);
            return;
        }

        setSourceFieldsOptions((prev) => [
            ...prev,
            { key: newVariable.name, label: newVariable.name, isDynamic: true },
        ]);

        setFieldList((prev) => [
            ...prev,
            {
                source: newVariable.name,
                sourceType: newVariable.name,
                sourceKey: newVariable.name,
                name: newVariable.name,
                dynamicMode: newVariable.mode,
                fields: newVariable.mode === "basic" ? newVariable.fields : [],
                advancedPayload:
                    newVariable.mode === "advanced"
                        ? newVariable.payload
                        : undefined,
                is_dynamic: true,
                options: {
                    isOpen: false,
                    functions: [],
                    default: "",
                    prefix: "",
                },
                destination: {},
                sourceErrors: true,
                destinationErrors: true,
            },
        ]);
        setIsDynamicValueOpen(false);
        scrollMappingToBottom();
    };

    const handleSourceFieldChange = (e, index, option, isTextField) => {
        if (!option && !isTextField) {
            setFieldList((prev) => {
                const newList = [...prev];
                newList[index].source = {};
                newList[index].sourceType = null;
                newList[index].sourceKey = null;
                newList[index].is_dynamic = false;
                newList[index].options.functions = [];
                newList[index].options.default = "";
                newList[index].options.prefix = "";
                newList[index].sourceErrors = false;
                return newList;
            });
            return;
        }

        const selectedKey = isTextField
            ? e?.target?.value
            : getSourceFieldIdentity(option);
        // Facebook: keep the raw Meta type and raw key separately from the UI
        // identity so the save body sends `type: <Meta enum>` and
        // `key: <question/locale key>`. Other sources keep key as type/key.
        const useRawFacebookParts =
            isFacebookSource && !isTextField && option && !option.isDynamic;
        const selectedType =
            useRawFacebookParts && option.type != null && option.type !== ""
                ? option.type.toString()
                : selectedKey;
        const selectedSourceKey =
            useRawFacebookParts && option.key != null && option.key !== ""
                ? option.key.toString()
                : selectedKey;

        setFieldList((prev) => {
            const newList = [...prev];
            newList[index].source = selectedKey;
            newList[index].sourceType = selectedType;
            newList[index].sourceKey = selectedSourceKey;
            newList[index].is_dynamic = !isTextField && !!option?.isDynamic;
            newList[index].options.functions = [];
            newList[index].options.default = "";
            newList[index].options.prefix = "";
            newList[index].sourceErrors = true;
            return newList;
        });

        setSourceError(false);
    };

    const handleDestinationFieldChange = (e, index, option) => {
        if (!option) {
            setFieldList((prev) => {
                const newList = [...prev];
                newList[index].destination = {};
                newList[index].destinationErrors = true;
                return newList;
            });
            return;
        }

        setFieldList((prev) => {
            const newList = [...prev];
            newList[index].destination = option.type;
            newList[index].destinationErrors = false;
            return newList;
        });

        setDestinationError(false);
    };
    const handleClickComparisationPart = (index) => {
        setIsComparisationFieldModalOpen(true);
        setComparisationFieldIndex(index);
        setFieldList((prev) => {
            const newList = [...prev];
            newList[index].options.isOpen = !newList[index].options.isOpen;

            if (!newList[index]?.compared_mapping) {
                newList[index].compared_mapping = [
                    {
                        [getRowIdentity(newList[index])]: [],
                    },
                ];
            }

            return newList;
        });
    };

    const handleCloseEvery = (index) => {
        setFieldList((prevList) =>
            prevList.map((item, i) => ({
                ...item,
                options: {
                    ...item.options,
                    isOpen: i === index ? !item.options.isOpen : false,
                },
            })),
        );
    };

    const handleSelectedFunctions = (e, index) => {
        setFieldList((prev) => {
            const newList = [...prev];
            newList[index].options.functions = e?.target?.value;
            return newList;
        });
    };

    const handleAddedDefaults = (e, index) => {
        const { value } = e.target;
        setFieldList((prev) => {
            const newList = [...prev];
            newList[index].options.default = value;
            return newList;
        });
    };

    const handleAddedPrefix = (e, index) => {
        const { value } = e.target;
        setFieldList((prev) => {
            const newList = [...prev];
            newList[index].options.prefix = value;
            return newList;
        });
    };

    const handleFieldAdd = (data) => {
        setDestinationError(false);
        setSourceError(false);
        if (data.custom) {
            setFieldList((prev) => [
                ...prev,
                {
                    custom: "true",
                    source: "",
                    options: {
                        isOpen: false,
                        functions: [],
                        default: "",
                        prefix: "",
                    },
                    destination: {},
                    destinationErrors: true,
                    sourceErrors: true,
                },
            ]);
        } else {
            setFieldList((prev) => [
                ...prev,
                {
                    source: {},
                    sourceType: "",
                    sourceKey: "",
                    options: {
                        isOpen: false,
                        functions: [],
                        default: "",
                        prefix: "",
                    },
                    destination: {},
                    destinationErrors: true,
                    sourceErrors: true,
                },
            ]);
        }
        scrollMappingToBottom();
    };
    const handleFieldRemove = (index) => {
        setFieldList((prev) => {
            const newList = [...prev];
            newList.splice(index, 1);
            return newList;
        });
    };
    const [functionsLeftValues, setFunctionsLeftValues] = useState({});
    const [functionsRightValues, setFunctionsRightValues] = useState({});

    const toSave = async () => {
        let error = false;
        const field_mapping = {};

        fieldList.length > 0 &&
            fieldList.forEach((item, index) => {
                let itemSource =
                    item.custom && item.source === "" ? "-" : item.source;
                // Group key: static rows use their value; Facebook advertiser
                // questions (type CUSTOM) use the question key so each one
                // gets its own entry; everything else uses the type token.
                const isFacebookCustomRow =
                    isFacebookSource &&
                    !item.custom &&
                    !item.is_dynamic &&
                    isFacebookCustomType(item.sourceType);
                const sourceType = item.custom
                    ? itemSource
                    : isFacebookCustomRow
                      ? getRowIdentity(item)
                      : item.sourceType;
                if (!field_mapping[sourceType]) {
                    field_mapping[sourceType] = [];
                }

                field_mapping[sourceType].push(
                    item.custom
                        ? {
                              is_static: true,
                              destination: item.destination,
                              key: [itemSource],
                              default: item?.options?.default
                                  ? item?.options?.default
                                  : "",
                              prefix: item?.options?.prefix
                                  ? item?.options?.prefix
                                  : "",
                              fn: item?.options?.functions.map((fn) => {
                                  if (fn) {
                                      if (fn === "max_len") {
                                          return {
                                              fn_name: fn,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsMaxLenValues[
                                                          index
                                                      ],
                                                  ),
                                              },
                                          };
                                      } else if (fn === "pad_with") {
                                          return {
                                              fn_name: fn,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsPadWithValues[
                                                          index
                                                      ],
                                                  ),
                                                  character:
                                                      functionsPadWithCharacter[
                                                          index
                                                      ],
                                              },
                                          };
                                      } else if (fn?.fn_name === "max_len") {
                                          return {
                                              fn_name: fn.fn_name,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsMaxLenValues[
                                                          index
                                                      ],
                                                  ),
                                              },
                                          };
                                      } else if (fn?.fn_name === "pad_with") {
                                          return {
                                              fn_name: fn.fn_name,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsPadWithValues[
                                                          index
                                                      ],
                                                  ),
                                                  character:
                                                      functionsPadWithCharacter[
                                                          index
                                                      ],
                                              },
                                          };
                                      } else if (fn === "left") {
                                          return {
                                              fn_name: fn,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsLeftValues[
                                                          index
                                                      ],
                                                  ),
                                              },
                                          };
                                      } else if (fn === "right") {
                                          return {
                                              fn_name: fn,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsRightValues[
                                                          index
                                                      ],
                                                  ),
                                              },
                                          };
                                      } else if (fn?.fn_name !== undefined) {
                                          return {
                                              fn_name: fn.fn_name,
                                              fn_values: {},
                                          };
                                      } else {
                                          return {
                                              fn_name: fn,
                                              fn_values: {},
                                          };
                                      }
                                  }
                              }),
                          }
                        : {
                              destination: item.destination,
                              key: item?.sourceKey,
                              default: item?.options?.default
                                  ? item?.options?.default
                                  : "",
                              prefix: item?.options?.prefix
                                  ? item?.options?.prefix
                                  : "",
                              fn: item?.options?.functions.map((fn) => {
                                  if (fn) {
                                      if (fn === "max_len") {
                                          return {
                                              fn_name: fn,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsMaxLenValues[
                                                          index
                                                      ],
                                                  ),
                                              },
                                          };
                                      } else if (fn === "pad_with") {
                                          return {
                                              fn_name: fn,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsPadWithValues[
                                                          index
                                                      ],
                                                  ),
                                                  character:
                                                      functionsPadWithCharacter[
                                                          index
                                                      ],
                                              },
                                          };
                                      } else if (fn?.fn_name === "max_len") {
                                          return {
                                              fn_name: fn.fn_name,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsMaxLenValues[
                                                          index
                                                      ],
                                                  ),
                                              },
                                          };
                                      } else if (fn?.fn_name === "pad_with") {
                                          return {
                                              fn_name: fn.fn_name,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsPadWithValues[
                                                          index
                                                      ],
                                                  ),
                                                  character:
                                                      functionsPadWithCharacter[
                                                          index
                                                      ],
                                              },
                                          };
                                      } else if (fn === "left") {
                                          return {
                                              fn_name: fn,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsLeftValues[
                                                          index
                                                      ],
                                                  ),
                                              },
                                          };
                                      } else if (fn === "right") {
                                          return {
                                              fn_name: fn,
                                              fn_values: {
                                                  length: parseInt(
                                                      functionsRightValues[
                                                          index
                                                      ],
                                                  ),
                                              },
                                          };
                                      } else if (fn?.fn_name !== undefined) {
                                          return {
                                              fn_name: fn.fn_name,
                                              fn_values: {},
                                          };
                                      } else {
                                          return {
                                              fn_name: fn,
                                              fn_values: {},
                                          };
                                      }
                                  }
                              }),
                              ...(item.is_dynamic ? { is_dynamic: true } : {}),

                              ...(item.is_dynamic
                                  ? {
                                        dynamic_field_name: item.sourceKey,
                                    }
                                  : {}),
                          },
                );
            });

        fieldList.forEach((item, index) => {
            if (
                Object.keys(item.source).length === 0 &&
                Object.keys(item.destination).length === 0
            ) {
                error = true;
                setDestinationError(true);
                setSourceError(true);
            } else if (
                Object.keys(item.source).length !== 0 &&
                Object.keys(item.destination).length === 0
            ) {
                error = true;
                setDestinationError(true);
                setSourceError(false);
            } else if (
                !item.custom &&
                Object.keys(item.source).length === 0 &&
                Object.keys(item.destination).length !== 0
            ) {
                error = true;
                setDestinationError(false);
                setSourceError(true);
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message:
                        language?.lead?.please_select_source_field ??
                        "Please select source field!",
                    icon: "info",
                });
            } else if (tempName === "") {
                error = true;
                setTempError(true);
            }
        });

        const comparedFields = {};
        fieldList.forEach((item) => {
            if (
                !item?.compared_fields ||
                !Object.keys(item?.compared_fields)?.length
            )
                return;

            const comparisationKey = Object.keys(item?.compared_fields)[0];
            if (comparisationKey) {
                comparedFields[comparisationKey] =
                    item?.compared_fields[comparisationKey];
            }
        });
        let data = {
            destination_id: stepOnePostDatas?.destination_id ?? "",
            field_mapping: field_mapping,
            ...(!!Object.keys(comparedFields).length
                ? { compared_fields: [comparedFields] }
                : {}),
            mapping_id: AllFields?.mapping_id,
        };
        if (!error) {
            saveFieldMapping(data);
        }
    };

    const saveFieldMapping = async (data) => {
        setIsLoading(true);
        let response = await saveFieldMappingAsync(data);
        if (response.status) {
            if (isSaveTemplate) {
                await saveTemp(response?.data ?? "");
            } else {
                setIsLoading(false);
            }
            props.nextStep();
        } else {
            setIsLoading(false);
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    const saveTemp = async (mappingData) => {
        setIsLoading(true);
        let data = {
            field_mapping_id: mappingData?.field_mapping_id ?? "",
            name: tempName,
            status: 1,
        };
        let response = await saveTempAsync(data);
        if (response.status) {
            setIsLoading(false);
            const savedTemplate = response?.data ?? {};
            dispatch(templateData(savedTemplate));
            const newTemplate = {
                id: savedTemplate?.id ?? savedTemplate?.template_id,
                name: savedTemplate?.name ?? tempName,
            };
            if (newTemplate.id && typeof props.setTemplates === "function") {
                props.setTemplates((prev) => [
                    ...(Array.isArray(prev) ? prev : []),
                    newTemplate,
                ]);
            }
            setIsSaveTemplate(false);
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message:
                    language?.lead?.saved_succesfully ?? "Saved succesfully",
                icon: "success",
                timer: 1000,
            });
        } else {
            setIsLoading(false);
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    const selectedDestinations =
        fieldList &&
        fieldList.length > 0 &&
        fieldList.map((field) => field.destination);

    const isArrayControl = (arr) => {
        if (
            Array.isArray(arr) &&
            arr.length === 1 &&
            Array.isArray(arr[0]) &&
            arr[0].length === 0
        ) {
            return false;
        } else {
            return Array.isArray(arr) && arr.length > 0;
        }
    };

    const [functionsMaxLenValues, setFunctionsMaxLenValues] = useState({});
    const [functionsPadWithValues, setFunctionsPadWithValues] = useState({});
    const [functionsPadWithCharacter, setFunctionsPadWithCharacter] = useState(
        {},
    );
    const [functionsReplaceSearchValues, setFunctionsReplaceSearchValues] =
        useState({});
    const [functionsReplaceWithValues, setFunctionsReplaceWithValues] =
        useState({});
    const [functionsSubstringStartValues, setFunctionsSubstringStartValues] =
        useState({});
    const [functionsSubstringEndValues, setFunctionsSubstringEndValues] =
        useState({});

    const handleFunctionValueChange = (index, key, value) => {
        const setters = {
            maxLen: setFunctionsMaxLenValues,
            padWithLength: setFunctionsPadWithValues,
            padWithCharacter: setFunctionsPadWithCharacter,
            left: setFunctionsLeftValues,
            right: setFunctionsRightValues,
        };
        setters[key]?.((prev) => ({ ...prev, [index]: value }));
    };

    const handleImportMappingClick = () => {
        importFileInputRef.current?.click();
    };

    const handleImportMappingFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }

        let parsed;
        try {
            const text = await file.text();
            parsed = JSON.parse(text);
        } catch {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message:
                    language?.lead_wizard?.invalid_mapping_file ??
                    "Please select a valid mapping JSON file.",
                icon: "error",
            });
            return;
        }

        const validationError = validateImportJson(parsed);
        if (validationError) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: validationError,
                icon: "error",
            });
            return;
        }

        const mappingIds = Array.isArray(AllFields?.mapping_id)
            ? AllFields.mapping_id
            : AllFields?.mapping_id
              ? [AllFields.mapping_id]
              : [];

        setIsImportingMapping(true);
        const response = await importFieldMapping({
            destination_id: stepOnePostDatas?.destination_id ?? "",
            mapping_id: mappingIds,
            import_json: parsed,
        });

        if (!response.status) {
            setIsImportingMapping(false);
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message && response.message,
                icon: "error",
            });
            return;
        }

        const applied = applyImportedMappings({
            mappings: parsed.mappings,
            sourceFieldsOptions,
            destinationFields: memoizedDestinationFields,
            allFunctions,
            compared_fields: parsed.compared_fields,
            source_dynamic_fields: parsed.source_dynamic_fields,
            isFacebookSource,
        });

        if (!applied.fieldList.length) {
            setIsImportingMapping(false);
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message:
                    language?.lead_wizard?.import_no_matching_fields ??
                    "None of the imported source fields match this source.",
                icon: "error",
            });
            return;
        }

        const newMappingId = extractImportedMappingId(response.data);
        if (newMappingId !== undefined) {
            skipFieldListResetRef.current = true;
            importedDynamicNamesRef.current = applied.dynamicNames;
            dispatch(
                destinationAndSourceFields({
                    ...AllFields,
                    mapping_id: newMappingId,
                }),
            );
        }

        setSourceFieldsOptions((prev) =>
            mergeDynamicSourceOptions(prev, applied.dynamicNames),
        );
        setFieldList(applied.fieldList);
        setFunctionsMaxLenValues(applied.functionValues.maxLen);
        setFunctionsPadWithValues(applied.functionValues.padWithLength);
        setFunctionsPadWithCharacter(applied.functionValues.padWithCharacter);
        setFunctionsLeftValues(applied.functionValues.left);
        setFunctionsRightValues(applied.functionValues.right);
        setSelectedTemplate(null);
        setIsImportingMapping(false);
    };

    const badgeCreator = (item) => {
        let fn = item.options?.functions?.map((x) => x?.fn_name);
        if (item.options?.default !== null && item.options?.default !== "") {
            fn.push(item.options?.default);
        }
        if (item.options?.prefix !== null && item.options?.prefix !== "") {
            fn.push(item.options?.prefix);
        }

        let totalCount = fn.length;

        const rowIdentity = getRowIdentity(item);
        if (
            item.compared_fields &&
            Array.isArray(item.compared_fields[rowIdentity]) &&
            item.compared_fields[rowIdentity].length > 0
        ) {
            totalCount += 1;
        }

        if (Array.isArray(fn)) {
            return totalCount;
        } else {
            return 0;
        }
    };
    const handleEditDynamicValue = (index) => {
        setIsDynamicValueOpen(true);
        setDynamicValueIndex(index);
    };

    const platformCode = stepOnePostDatas?.source_type_code;

    const testMappingIds = Array.isArray(AllFields?.mapping_id)
        ? AllFields.mapping_id
        : AllFields?.mapping_id
          ? [AllFields.mapping_id]
          : [];

    const hasValidMappings = fieldList.some(
        (item) =>
            Object.keys(item?.destination || {}).length > 0 &&
            (item?.custom || Object.keys(item?.source || {}).length > 0),
    );

    const testSourceKeys = Array.from(
        new Set(
            fieldList
                .filter((item) => !item?.custom && getRowIdentity(item))
                .map((item) => getRowIdentity(item)),
        ),
    );

    const isTestDisabled =
        !hasValidMappings || testMappingIds.length === 0 || isTesting;

    const testTooltipTitle = !hasValidMappings
        ? (language?.lead?.test_run_disabled_tooltip ??
          "Add at least one mapping to run a test.")
        : (language?.lead?.test_run_enabled_tooltip ??
          "Run a test with your current mappings.");

    return (
        <>
            <WizardStepLayout
                primaryTestId="lead-mapping-next"
                backTestId="lead-mapping-back"
                ref={stepLayoutRef}
                maxWidth="100%"
                onBack={toPrevious}
                backLabel={language?.buttons?.back ?? "Back"}
                onPrimary={() => {
                    resetOpenStates();
                    toSave();
                }}
                primaryLabel={"Next"}
                isLoading={isLoading}
                renderPrimaryExtras={() => (
                    <Tooltip title={testTooltipTitle}>
                        <span>
                            <Button
                                className="py-2"
                                variant="outlined"
                                color="primary"
                                onClick={() => setIsTestModalOpen(true)}
                                disabled={isTestDisabled}
                            >
                                {isTesting ? (
                                    <ClipLoader size={20} loading={isTesting} />
                                ) : (
                                    (language?.buttons?.test ?? "Test")
                                )}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            >
                <div>
                    {isArrayControl(props?.templates) && (
                        <Box
                            className="mb-4"
                            style={{
                                maxWidth: "1280px",
                            }}
                        >
                            <div className="wizard-title-area mb-3">
                                <p>
                                    <strong>
                                        {language?.lead_wizard?.templates ??
                                            "Templates"}
                                    </strong>
                                    {language?.lead_wizard?.templates_desc ??
                                        "Templates let you reuse a previously saved field mapping. Select one to prefill the mapping below instead of setting it up from scratch."}
                                </p>
                            </div>
                            <FormControl fullWidth>
                                <InputLabel id="template-select-label">
                                    {language?.lead_wizard?.select_template ??
                                        "Select Template"}
                                </InputLabel>
                                <Select
                                    labelId="template-select-label"
                                    label={
                                        language?.lead_wizard
                                            ?.select_template ??
                                        "Select Template"
                                    }
                                    onChange={(e) => {
                                        handleSelectedTemplates(e);
                                    }}
                                    value={selectedTemplate}
                                >
                                    {props?.templates.map((item) => (
                                        <MenuItem key={item.id} value={item.id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                    {isComparisationFieldModalOpen && (
                        <AddComparisationModal
                            matchFields={fieldList}
                            setMatchFields={setFieldList}
                            isOpen={isComparisationFieldModalOpen}
                            setIsOpen={setIsComparisationFieldModalOpen}
                            fieldIndex={comparisationFieldIndex}
                        />
                    )}
                    {isDynamicValueOpen && (
                        <CreateDynamicValueModal
                            isOpen={isDynamicValueOpen}
                            onClose={() => setIsDynamicValueOpen(false)}
                            onSubmit={onDynamicVariableCreate}
                            availableSourceFields={sourceFieldsOptions.map(
                                (field) => ({
                                    value: getSourceFieldIdentity(field),
                                    label: field.label,
                                }),
                            )}
                            allFunctions={allFunctions}
                            editedField={
                                dynamicValueIndex !== null &&
                                fieldList?.[dynamicValueIndex]
                                    ? fieldList[dynamicValueIndex]
                                          .dynamicMode === "advanced"
                                        ? {
                                              name: fieldList[dynamicValueIndex]
                                                  .name,
                                              mode: "advanced",
                                              payload: normalizeAdvancedPayload(
                                                  fieldList[dynamicValueIndex]
                                                      .advancedPayload || "",
                                              ),
                                          }
                                        : {
                                              name: fieldList[dynamicValueIndex]
                                                  .name,
                                              mode: "basic",
                                              fields: (
                                                  fieldList[dynamicValueIndex]
                                                      .fields || []
                                              ).map((field) => {
                                                  const value =
                                                      toSourceFieldIdentity(
                                                          field.value,
                                                      );
                                                  const option =
                                                      sourceFieldsOptions.find(
                                                          (opt) =>
                                                              getSourceFieldIdentity(
                                                                  opt,
                                                              ) ===
                                                              String(value),
                                                      );
                                                  return {
                                                      ...field,
                                                      value,
                                                      label:
                                                          option?.label ||
                                                          field.label,
                                                  };
                                              }),
                                          }
                                    : undefined
                            }
                            editFieldIndex={dynamicValueIndex}
                            notAvailableFieldNames={fieldList?.map(
                                (field) => field.name || getRowIdentity(field),
                            )}
                        />
                    )}

                    <div className="row">
                        <div className="col-12">
                            <input
                                ref={importFileInputRef}
                                type="file"
                                accept=".json,application/json"
                                hidden
                                onChange={handleImportMappingFile}
                            />
                            <Box
                                display="flex"
                                alignItems="flex-start"
                                justifyContent="space-between"
                                gap={2}
                                className="mb-3"
                            >
                                <div className="wizard-title-area mb-0">
                                    <p>
                                        <strong>
                                            {language?.lead_wizard?.mapping ??
                                                "Mapping"}
                                        </strong>
                                        {language?.lead_wizard?.mapping_desc ??
                                            "Map each source field to a destination field. You can also add static values or dynamic values built from multiple fields and functions."}
                                    </p>
                                </div>
                                <StandartButton
                                    variant={ButtonVariant.BLACK_OUTLINED}
                                    onClick={handleImportMappingClick}
                                    disabled={isImportingMapping}
                                    isLoading={isImportingMapping}
                                    wrapperSx={{ flexShrink: 0 }}
                                >
                                    {language?.lead_wizard?.import_mapping ??
                                        "Import Mappings"}
                                </StandartButton>
                            </Box>
                            <FormGroup
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    width: "fit-content",
                                    position: "relative",
                                }}
                            >
                                <FormControlLabel
                                    className="tour-group-fields-checkbox"
                                    style={{ maxWidth: "fit-content" }}
                                    control={
                                        <Checkbox
                                            checked={isGroupState}
                                            onChange={
                                                onChangeGroupSimilarFields
                                            }
                                            disabled={
                                                isLoadingGroupSimilarFields
                                            }
                                        />
                                    }
                                    label={
                                        language?.lead_wizard
                                            ?.group_similar_fields ??
                                        "Group similar fields"
                                    }
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        left: "50%",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1000,
                                    }}
                                >
                                    <ClipLoader
                                        color="#000"
                                        loading={isLoadingGroupSimilarFields}
                                        size={20}
                                    />
                                </div>
                            </FormGroup>
                        </div>
                    </div>

                    {fieldList.length > 0 &&
                        fieldList.map((item, index) => {
                            return (
                                <>
                                    <Box
                                        data-cy="lead-mapping-row"
                                        className="tour-mapping-row"
                                        sx={{
                                            ...(isMobile && {
                                                backgroundColor:
                                                    "background.paper",
                                                borderRadius: 3,
                                                boxShadow:
                                                    "0 4px 12px rgba(0,0,0,0.08)",
                                                border: "1px solid",
                                                borderColor: "divider",
                                                p: { xs: 2, sm: 3 },
                                                mb: 3,
                                                mx: 0,
                                                transition:
                                                    "all 0.2s ease-in-out",
                                                "&:hover": {
                                                    boxShadow:
                                                        "0 6px 16px rgba(0,0,0,0.12)",
                                                    transform:
                                                        "translateY(-1px)",
                                                },
                                            }),
                                            ...(!isMobile && {
                                                my: 4,
                                            }),
                                        }}
                                    >
                                        {isMobile && (
                                            <Box
                                                sx={{
                                                    mb: 2,
                                                    pb: 1,
                                                    borderBottom: "1px solid",
                                                    borderColor: "divider",
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: "text.primary",
                                                    }}
                                                >
                                                    Field Mapping {index + 1}
                                                </Typography>
                                                {fieldList.length !== 1 && (
                                                    <IconButton
                                                        onClick={() =>
                                                            handleFieldRemove(
                                                                index,
                                                            )
                                                        }
                                                        size="small"
                                                        aria-label="Remove mapping"
                                                        sx={
                                                            connectionSectionDeleteButtonSx
                                                        }
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        )}
                                        <div className="row">
                                            <div
                                                className={`${
                                                    isMobile
                                                        ? "col-12 mb-3"
                                                        : "col-4"
                                                }`}
                                            >
                                                {isMobile && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "text.secondary",
                                                            fontWeight: 500,
                                                            mb: 1,
                                                            display: "block",
                                                        }}
                                                    >
                                                        Source Field
                                                    </Typography>
                                                )}
                                                {item.custom && (
                                                    <>
                                                        <FormControl
                                                            className="mb-3"
                                                            style={{
                                                                width: "100%",
                                                            }}
                                                        >
                                                            <TextField
                                                                name="name"
                                                                key={index}
                                                                label={
                                                                    language
                                                                        ?.lead
                                                                        ?.add_static_value ??
                                                                    "Add a Static Value"
                                                                }
                                                                variant="outlined"
                                                                onChange={(e) =>
                                                                    handleSourceFieldChange(
                                                                        e,
                                                                        index,
                                                                        undefined,
                                                                        true,
                                                                    )
                                                                }
                                                                placeholder={""}
                                                                value={
                                                                    item.source
                                                                }
                                                                sx={{
                                                                    "& .MuiOutlinedInput-root":
                                                                        {
                                                                            fontSize:
                                                                                isMobile
                                                                                    ? "16px"
                                                                                    : "16px",
                                                                            minHeight:
                                                                                isMobile
                                                                                    ? "56px"
                                                                                    : "56px",
                                                                        },
                                                                    "& .MuiInputLabel-root":
                                                                        {
                                                                            fontSize:
                                                                                isMobile
                                                                                    ? "14px"
                                                                                    : "14px",
                                                                        },
                                                                }}
                                                            />
                                                        </FormControl>
                                                    </>
                                                )}
                                                {!item.custom && (
                                                    <>
                                                        <Form.Group controlId="exampleForm.ControlInput1">
                                                            <FormControl
                                                                style={{
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                <Autocomplete
                                                                    key={index}
                                                                    options={
                                                                        sourceFieldsOptions ||
                                                                        []
                                                                    }
                                                                    size="medium"
                                                                    getOptionLabel={(
                                                                        option,
                                                                    ) =>
                                                                        option?.label ||
                                                                        ""
                                                                    }
                                                                    value={
                                                                        sourceFieldsOptions?.find(
                                                                            (
                                                                                field,
                                                                            ) =>
                                                                                getSourceFieldIdentity(
                                                                                    field,
                                                                                ) ===
                                                                                item.source,
                                                                        ) ||
                                                                        null
                                                                    }
                                                                    onChange={(
                                                                        event,
                                                                        newValue,
                                                                    ) => {
                                                                        handleSourceFieldChange(
                                                                            event,
                                                                            index,
                                                                            newValue,
                                                                        );
                                                                    }}
                                                                    renderOption={(
                                                                        props,
                                                                        option,
                                                                    ) => (
                                                                        <li
                                                                            {...props}
                                                                            key={getSourceFieldIdentity(
                                                                                option,
                                                                            )}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    display:
                                                                                        "flex",
                                                                                    alignItems:
                                                                                        "center",
                                                                                    justifyContent:
                                                                                        "space-between",
                                                                                    width: "100%",
                                                                                }}
                                                                            >
                                                                                <span>
                                                                                    {option.label ||
                                                                                        ""}
                                                                                </span>
                                                                                {option.isDynamic && (
                                                                                    <PodcastsOutlinedIcon
                                                                                        sx={{
                                                                                            fontSize: 18,
                                                                                            color: "#ed6c02",
                                                                                            ml: 1,
                                                                                            flexShrink: 0,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </Box>
                                                                        </li>
                                                                    )}
                                                                    renderInput={(
                                                                        params,
                                                                    ) => (
                                                                        <TextField
                                                                            {...params}
                                                                            data-cy="lead-mapping-source-field"
                                                                            label={
                                                                                language
                                                                                    ?.lead
                                                                                    ?.select_source_field ??
                                                                                "Select Source Field"
                                                                            }
                                                                            error={
                                                                                sourceError &&
                                                                                Object.keys(
                                                                                    item.source ||
                                                                                        {},
                                                                                )
                                                                                    .length ===
                                                                                    0
                                                                            }
                                                                            sx={{
                                                                                "& .MuiOutlinedInput-root":
                                                                                    {
                                                                                        fontSize:
                                                                                            isMobile
                                                                                                ? "16px"
                                                                                                : "16px",
                                                                                        minHeight:
                                                                                            isMobile
                                                                                                ? "56px"
                                                                                                : "56px",
                                                                                    },
                                                                                "& .MuiInputLabel-root":
                                                                                    {
                                                                                        fontSize:
                                                                                            isMobile
                                                                                                ? "14px"
                                                                                                : "14px",
                                                                                    },
                                                                            }}
                                                                        />
                                                                    )}
                                                                    isOptionEqualToValue={(
                                                                        option,
                                                                        value,
                                                                    ) =>
                                                                        getSourceFieldIdentity(
                                                                            option,
                                                                        ) ===
                                                                        getSourceFieldIdentity(
                                                                            value,
                                                                        )
                                                                    }
                                                                />
                                                            </FormControl>
                                                        </Form.Group>
                                                        {sourceError &&
                                                            Object.keys(
                                                                item.source,
                                                            ).length === 0 && (
                                                                <span className="error-message">
                                                                    Please
                                                                    Select a
                                                                    Source!
                                                                </span>
                                                            )}
                                                    </>
                                                )}
                                            </div>
                                            <div
                                                className={`${
                                                    isMobile
                                                        ? "col-12 mb-3 d-flex justify-content-center"
                                                        : "col-3 d-flex justify-content-center"
                                                }`}
                                            >
                                                <div className="row text-center">
                                                    <div className="col-12 py-2">
                                                        {isMobile ? (
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    justifyContent:
                                                                        "center",
                                                                    alignItems:
                                                                        "center",
                                                                }}
                                                            >
                                                                <IconButton
                                                                    onClick={() =>
                                                                        handleCloseEvery(
                                                                            index,
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        position:
                                                                            "relative",
                                                                        color: "warning.main",
                                                                        "&:hover":
                                                                            {
                                                                                backgroundColor:
                                                                                    "warning.light",
                                                                                color: "warning.dark",
                                                                            },
                                                                        transition:
                                                                            "all 0.2s ease-in-out",
                                                                    }}
                                                                >
                                                                    <SettingsIcon />
                                                                    {badgeCreator(
                                                                        fieldList[
                                                                            index
                                                                        ],
                                                                    ) > 0 && (
                                                                        <Chip
                                                                            label={badgeCreator(
                                                                                fieldList[
                                                                                    index
                                                                                ],
                                                                            )}
                                                                            size="small"
                                                                            sx={{
                                                                                position:
                                                                                    "absolute",
                                                                                top: -8,
                                                                                right: -8,
                                                                                height: 20,
                                                                                minWidth: 20,
                                                                                fontSize:
                                                                                    "0.75rem",
                                                                                backgroundColor:
                                                                                    "error.main",
                                                                                color: "white",
                                                                                "& .MuiChip-label":
                                                                                    {
                                                                                        px: 0.5,
                                                                                    },
                                                                            }}
                                                                            onClick={() => {}}
                                                                        />
                                                                    )}
                                                                </IconButton>
                                                            </Box>
                                                        ) : (
                                                            <StandartButton
                                                                variant={
                                                                    ButtonVariant.BLACK_OUTLINED
                                                                }
                                                                onClick={() => {
                                                                    setOptionsHover(
                                                                        null,
                                                                    );
                                                                    handleCloseEvery(
                                                                        index,
                                                                    );
                                                                }}
                                                                onMouseEnter={(
                                                                    e,
                                                                ) =>
                                                                    setOptionsHover(
                                                                        {
                                                                            index,
                                                                            anchorEl:
                                                                                e.currentTarget,
                                                                        },
                                                                    )
                                                                }
                                                                onMouseLeave={() =>
                                                                    setOptionsHover(
                                                                        null,
                                                                    )
                                                                }
                                                            >
                                                                Options
                                                                {badgeCreator(
                                                                    fieldList[
                                                                        index
                                                                    ],
                                                                ) > 0 && (
                                                                    <Chip
                                                                        label={badgeCreator(
                                                                            fieldList[
                                                                                index
                                                                            ],
                                                                        )}
                                                                        size="small"
                                                                        sx={{
                                                                            position:
                                                                                "absolute",
                                                                            top: -8,
                                                                            right: -8,
                                                                            height: 20,
                                                                            minWidth: 20,
                                                                            fontSize:
                                                                                "0.75rem",
                                                                            backgroundColor:
                                                                                "error.main",
                                                                            color: "white",
                                                                            "& .MuiChip-label":
                                                                                {
                                                                                    px: 0.5,
                                                                                },
                                                                        }}
                                                                        onClick={() => {}}
                                                                    />
                                                                )}
                                                            </StandartButton>
                                                        )}
                                                    </div>
                                                    <FieldOptionsSummaryPopover
                                                        open={
                                                            optionsHover?.index ===
                                                            index
                                                        }
                                                        anchorEl={
                                                            optionsHover?.anchorEl ??
                                                            null
                                                        }
                                                        item={{
                                                            ...item,
                                                            sourceKey:
                                                                getRowIdentity(
                                                                    item,
                                                                ),
                                                        }}
                                                        allFunctions={
                                                            allFunctions
                                                        }
                                                        functionValues={{
                                                            maxLen: functionsMaxLenValues[
                                                                index
                                                            ],
                                                            padWithLength:
                                                                functionsPadWithValues[
                                                                    index
                                                                ],
                                                            padWithCharacter:
                                                                functionsPadWithCharacter[
                                                                    index
                                                                ],
                                                            left: functionsLeftValues[
                                                                index
                                                            ],
                                                            right: functionsRightValues[
                                                                index
                                                            ],
                                                        }}
                                                        language={language}
                                                    />
                                                    <FieldOptionsModal
                                                        open={Boolean(
                                                            item.options.isOpen,
                                                        )}
                                                        onClose={() =>
                                                            handleCloseEvery(
                                                                index,
                                                            )
                                                        }
                                                        item={{
                                                            ...item,
                                                            sourceKey:
                                                                getRowIdentity(
                                                                    item,
                                                                ),
                                                        }}
                                                        index={index}
                                                        language={language}
                                                        allFunctions={
                                                            allFunctions
                                                        }
                                                        onFunctionsChange={(
                                                            values,
                                                        ) =>
                                                            handleSelectedFunctions(
                                                                {
                                                                    target: {
                                                                        value: values,
                                                                    },
                                                                },
                                                                index,
                                                            )
                                                        }
                                                        functionValues={{
                                                            maxLen: functionsMaxLenValues[
                                                                index
                                                            ],
                                                            padWithLength:
                                                                functionsPadWithValues[
                                                                    index
                                                                ],
                                                            padWithCharacter:
                                                                functionsPadWithCharacter[
                                                                    index
                                                                ],
                                                            left: functionsLeftValues[
                                                                index
                                                            ],
                                                            right: functionsRightValues[
                                                                index
                                                            ],
                                                        }}
                                                        onFunctionValueChange={(
                                                            key,
                                                            value,
                                                        ) =>
                                                            handleFunctionValueChange(
                                                                index,
                                                                key,
                                                                value,
                                                            )
                                                        }
                                                        onDefaultChange={(
                                                            value,
                                                        ) =>
                                                            handleAddedDefaults(
                                                                {
                                                                    target: {
                                                                        value,
                                                                    },
                                                                },
                                                                index,
                                                            )
                                                        }
                                                        onPrefixChange={(
                                                            value,
                                                        ) =>
                                                            handleAddedPrefix(
                                                                {
                                                                    target: {
                                                                        value,
                                                                    },
                                                                },
                                                                index,
                                                            )
                                                        }
                                                        onManageComparisons={() =>
                                                            handleClickComparisationPart(
                                                                index,
                                                            )
                                                        }
                                                        comparisonDisabled={
                                                            typeof item?.source !==
                                                            "string"
                                                        }
                                                        sourceFieldName={
                                                            typeof item?.source ===
                                                                "string" &&
                                                            item.source !== ""
                                                                ? (sourceFieldsOptions?.find(
                                                                      (f) =>
                                                                          getSourceFieldIdentity(
                                                                              f,
                                                                          ) ===
                                                                          item.source,
                                                                  )?.label ??
                                                                  item.source)
                                                                : ""
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className={`${
                                                    isMobile
                                                        ? "col-12 mb-3"
                                                        : "col-4"
                                                }`}
                                            >
                                                {isMobile && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "text.secondary",
                                                            fontWeight: 500,
                                                            mb: 1,
                                                            display: "block",
                                                        }}
                                                    >
                                                        Destination Field
                                                    </Typography>
                                                )}
                                                <Form.Group controlId="exampleForm.ControlInput1">
                                                    <FormControl
                                                        style={{
                                                            width: "100%",
                                                        }}
                                                    >
                                                        <Autocomplete
                                                            key={`destination-field-${index}-${item.destination || ""}`}
                                                            options={
                                                                memoizedDestinationFields
                                                            }
                                                            filterOptions={
                                                                destinationFilterOptions
                                                            }
                                                            size="medium"
                                                            getOptionKey={(
                                                                option,
                                                            ) =>
                                                                option?.type ||
                                                                ""
                                                            }
                                                            getOptionLabel={(
                                                                option,
                                                            ) =>
                                                                option?.name ||
                                                                ""
                                                            }
                                                            value={
                                                                memoizedDestinationFields?.find(
                                                                    (field) =>
                                                                        field.type ===
                                                                        item.destination,
                                                                ) || null
                                                            }
                                                            onChange={(
                                                                event,
                                                                newValue,
                                                            ) => {
                                                                handleDestinationFieldChange(
                                                                    event,
                                                                    index,
                                                                    newValue,
                                                                );
                                                            }}
                                                            isOptionEqualToValue={(
                                                                option,
                                                                value,
                                                            ) =>
                                                                option.type ===
                                                                value?.type
                                                            }
                                                            getOptionDisabled={(
                                                                option,
                                                            ) =>
                                                                selectedDestinations.includes(
                                                                    option.type,
                                                                )
                                                            }
                                                            renderOption={(
                                                                props,
                                                                option,
                                                            ) => {
                                                                const isDisabled =
                                                                    selectedDestinations.includes(
                                                                        option.type,
                                                                    );
                                                                return (
                                                                    <Styled.StyledOption
                                                                        {...props}
                                                                    >
                                                                        <span>
                                                                            {
                                                                                option.name
                                                                            }
                                                                        </span>
                                                                        {isDisabled && (
                                                                            <span className="in-use-label">
                                                                                {language
                                                                                    ?.lead_wizard
                                                                                    ?.in_use ??
                                                                                    "In Use"}
                                                                            </span>
                                                                        )}
                                                                    </Styled.StyledOption>
                                                                );
                                                            }}
                                                            renderInput={(
                                                                params,
                                                            ) => (
                                                                <TextField
                                                                    {...params}
                                                                    data-cy="lead-mapping-destination-field"
                                                                    label={
                                                                        language
                                                                            ?.lead
                                                                            ?.select_destination_field ??
                                                                        "Select Destination Field"
                                                                    }
                                                                    error={
                                                                        destinationError &&
                                                                        item.destinationErrors
                                                                    }
                                                                    className="custom-disabled-select tour-select-destination-field"
                                                                    sx={{
                                                                        "& .MuiOutlinedInput-root":
                                                                            {
                                                                                fontSize:
                                                                                    isMobile
                                                                                        ? "16px"
                                                                                        : "16px",
                                                                                minHeight:
                                                                                    isMobile
                                                                                        ? "56px"
                                                                                        : "56px",
                                                                            },
                                                                        "& .MuiInputLabel-root":
                                                                            {
                                                                                fontSize:
                                                                                    isMobile
                                                                                        ? "14px"
                                                                                        : "14px",
                                                                            },
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    </FormControl>
                                                </Form.Group>

                                                {destinationError &&
                                                    item.destinationErrors && (
                                                        <span className="error-message">
                                                            Please Select a
                                                            Destination!
                                                        </span>
                                                    )}
                                            </div>
                                            {!isMobile && (
                                                <div className="col-1 text-center d-flex justify-content-center align-items-center">
                                                    {fieldList.length !== 1 && (
                                                        <IconButton
                                                            onClick={() =>
                                                                handleFieldRemove(
                                                                    index,
                                                                )
                                                            }
                                                            size="small"
                                                            aria-label="Remove mapping"
                                                            sx={
                                                                connectionSectionDeleteButtonSx
                                                            }
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </Box>
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        gap={2}
                                        className="my-3"
                                        flexWrap="wrap"
                                        sx={{
                                            "& > .MuiBox-root": {
                                                width: {
                                                    xs: "100%",
                                                    sm: "auto",
                                                },
                                            },
                                            "& .MuiButton-root": {
                                                width: {
                                                    xs: "100%",
                                                    sm: "auto",
                                                },
                                            },
                                        }}
                                    >
                                        {fieldList.length - 1 === index &&
                                            fieldList.length < 5000 && (
                                                <Tooltip title="You can select a source field from the list which are fetched from the source.">
                                                    <Box>
                                                        <StandartButton
                                                            variant={
                                                                ButtonVariant.BLACK_OUTLINED
                                                            }
                                                            onClick={() =>
                                                                handleFieldAdd({
                                                                    custom: false,
                                                                })
                                                            }
                                                        >
                                                            {"Add Value"}
                                                        </StandartButton>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        {fieldList.length - 1 === index &&
                                            fieldList.length < 5000 && (
                                                <Tooltip title="You may define and assign a static source value through manual input.">
                                                    <Box>
                                                        <StandartButton
                                                            variant={
                                                                ButtonVariant.BLACK_OUTLINED
                                                            }
                                                            onClick={() =>
                                                                handleFieldAdd({
                                                                    custom: true,
                                                                })
                                                            }
                                                        >
                                                            <span>
                                                                {language
                                                                    ?.buttons
                                                                    ?.add_a_static_value ??
                                                                    "Add a Static Value"}
                                                            </span>
                                                        </StandartButton>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        {fieldList.length - 1 === index &&
                                            fieldList.length < 5000 && (
                                                <Tooltip title="You can add a dynamic value to the field, dynamic value may contain multiple fields and functions.">
                                                    <Box>
                                                        <StandartButton
                                                            variant={
                                                                ButtonVariant.BLACK_OUTLINED
                                                            }
                                                            onClick={() => {
                                                                setDynamicValueIndex(
                                                                    null,
                                                                );
                                                                setIsDynamicValueOpen(
                                                                    true,
                                                                );
                                                            }}
                                                        >
                                                            Add a Dynamic Value
                                                        </StandartButton>
                                                    </Box>
                                                </Tooltip>
                                            )}
                                    </Stack>
                                </>
                            );
                        })}

                    <Box
                        sx={{
                            width: {
                                xs: "100%",
                                lg: "100%",
                            },
                        }}
                    >
                        <div className="flex flex-col justify-content-start align-items-start mt-5">
                            <FormGroup>
                                <FormControlLabel
                                    className="tour-save-template-checkbox"
                                    control={
                                        <Checkbox
                                            checked={isSaveTemplate}
                                            onChange={
                                                checkHandlerIsSaveTemplate
                                            }
                                        />
                                    }
                                    label={
                                        language?.lead_wizard
                                            ?.save_as_template ??
                                        "Save as template"
                                    }
                                />
                            </FormGroup>

                            {isSaveTemplate && (
                                <>
                                    <FormControl className="mb-3 mt-3">
                                        <TextField
                                            name="name"
                                            label={
                                                language?.lead_wizard
                                                    ?.template_name ??
                                                "Template Name"
                                            }
                                            variant="outlined"
                                            onChange={(e) =>
                                                setTempName(e.target.value)
                                            }
                                            placeholder={"Source Name"}
                                            value={tempName}
                                            error={tempName === ""}
                                        />
                                    </FormControl>
                                    {tempError && (
                                        <span className="error-message">
                                            {language?.lead_wizard
                                                ?.please_write_name ??
                                                "Please write name!"}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    </Box>
                </div>
            </WizardStepLayout>
            <TestRunModal
                open={isTestModalOpen}
                onClose={() => setIsTestModalOpen(false)}
                mappingIds={testMappingIds}
                platformCode={platformCode}
                sourceKeys={testSourceKeys}
                language={language}
                onRunningChange={setIsTesting}
            />
        </>
    );
};

export default LeadsStep22;
